import { motion } from "framer-motion";
import { Store, TrendingUp, Users, Sparkles, ArrowRight, Mail, ExternalLink } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

const ease = [0.22, 1, 0.36, 1];
const ICONS = { b1: Users, b2: TrendingUp, b3: Sparkles };
const KEYS = ["b1", "b2", "b3"];

export const ForBusiness = () => {
  const { t } = useLanguage();
  return (
    <section id="for-business" data-testid="for-business-section" className="relative py-20 sm:py-28 lg:py-32 overflow-hidden">
      <div className="absolute inset-0 hero-backdrop opacity-60 pointer-events-none" />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ y: 22, opacity: 0 }} whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7, ease }}
          className="relative rounded-[28px] border border-white/10 bg-white/[0.04] backdrop-blur-xl p-6 sm:p-10 lg:p-14 shadow-[0_30px_90px_rgba(0,0,0,0.55)] overflow-hidden"
        >
          <div className="absolute -top-24 -left-24 w-[420px] h-[420px] rounded-full bg-[#39FF14]/10 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -right-24 w-[360px] h-[360px] rounded-full bg-[#7DD3FC]/8 blur-3xl pointer-events-none" />
          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            <div className="lg:col-span-6">
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#39FF14]/90">{t.forBusiness.eyebrow}</span>
              <h2 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl text-white mt-3 tracking-tight">
                {t.forBusiness.title1}<br />
                <span className="text-[#39FF14] drop-shadow-[0_0_24px_rgba(57,255,20,0.4)]">{t.forBusiness.title2}</span>
              </h2>
              <p className="mt-4 text-white/70 text-base sm:text-lg leading-relaxed max-w-lg">{t.forBusiness.subhead}</p>

              <div className="mt-7 flex flex-wrap gap-3">
                <a href="mailto:support@motogo.life?subject=Integrate%20my%20store%20with%20MotoGO" data-testid="integrate-store-cta-button"
                  className="group inline-flex items-center gap-2 rounded-xl bg-[#39FF14] text-black font-semibold px-5 py-3 hover:bg-[#6BFF55] transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#39FF14]/70 active:translate-y-[1px] shadow-[0_0_0_1px_rgba(57,255,20,0.35),0_18px_60px_rgba(0,0,0,0.35)]">
                  <Store className="w-4 h-4" />{t.forBusiness.ctaIntegrate}
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                </a>
                <a href="mailto:support@motogo.life?subject=Send%20us%20a%20message" data-testid="contact-private-cta-button"
                  className="inline-flex items-center gap-2 rounded-xl border border-white/12 bg-white/[0.06] text-white px-5 py-3 hover:bg-white/10 hover:border-white/20 transition-colors duration-200">
                  <Mail className="w-4 h-4" />{t.forBusiness.ctaMessage}
                </a>
              </div>

              <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/30 backdrop-blur-xl px-3 py-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#39FF14] shadow-[0_0_10px_rgba(57,255,20,0.7)]" />
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/75">{t.forBusiness.status}</span>
              </div>

              <a
                href="https://ultramoto.ro/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="partner-ultramoto-link"
                className="group mt-5 flex items-center justify-between gap-4 rounded-2xl border border-[#39FF14]/25 bg-[#39FF14]/[0.06] backdrop-blur-xl px-4 py-3.5 transition-colors hover:bg-[#39FF14]/[0.1] hover:border-[#39FF14]/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#39FF14]/70"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="inline-flex items-center justify-center w-10 h-10 shrink-0 rounded-xl overflow-hidden bg-black/60 border border-white/12">
                    <img src="/ultramoto-logo.png" alt="UltraMoto" className="w-full h-full object-contain p-1" loading="lazy" />
                  </span>
                  <div className="min-w-0">
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#39FF14]/90">{t.forBusiness.partnerLabel}</div>
                    <div className="font-display font-semibold text-white text-base truncate">{t.forBusiness.partnerName}</div>
                  </div>
                </div>
                <span className="inline-flex items-center gap-1.5 shrink-0 text-xs font-semibold text-white/80 group-hover:text-white transition-colors">
                  <span className="hidden sm:inline">{t.forBusiness.partnerVisit}</span>
                  <ExternalLink className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                </span>
              </a>
            </div>

            <div className="lg:col-span-6">
              <div className="grid grid-cols-1 gap-3 sm:gap-4">
                {KEYS.map((k, i) => {
                  const Icon = ICONS[k];
                  return (
                    <motion.div key={k}
                      initial={{ y: 14, opacity: 0 }} whileInView={{ y: 0, opacity: 1 }}
                      viewport={{ once: true }} transition={{ duration: 0.55, ease, delay: 0.1 + i * 0.08 }}
                      data-testid="for-business-benefit"
                      className="relative overflow-hidden flex items-start gap-4 rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-4 sm:p-5 transition-colors hover:bg-white/[0.07] hover:border-white/16"
                    >
                      <span className="inline-flex items-center justify-center w-11 h-11 shrink-0 rounded-xl bg-[#39FF14]/10 border border-[#39FF14]/30 text-[#39FF14]">
                        <Icon className="w-5 h-5" />
                      </span>
                      <div>
                        <div className="font-display font-semibold text-white text-base">{t.forBusiness[k].title}</div>
                        <div className="text-sm text-white/65 mt-1 leading-relaxed">{t.forBusiness[k].desc}</div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
